import { Clock } from "@/components/icons";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useT } from "@/app/localization";

// The dashboard formats every timestamp with the native Date API in the
// viewer's browser timezone (no explicit `timeZone:` is passed anywhere), so
// this chip simply surfaces that fact. It reads the zone the browser resolved
// for the current user and is purely informational — it does not change any
// formatting. Note: distinct from a unit's "ToU timezone", which only anchors
// its Time-of-Use schedule windows on the server.
function resolveBrowserTimezone(): string | null {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}

export function LocalTimezoneChip() {
    const t = useT();
    const tz = resolveBrowserTimezone() ?? t("common.time.local_zone_unknown");
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className="hidden md:inline-flex items-center gap-1.5 rounded-2xl px-2 py-1 text-xs text-foreground/60 hover:bg-muted transition-colors cursor-default"
                        aria-label={t("common.time.local_zone_label", { timezone: tz })}
                    >
                        <Clock className="h-3.5 w-3.5" />
                        <span className="font-mono">{tz}</span>
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    {t("common.time.local_zone_tooltip")}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
